import { CANDIDATE_ASSESSMENTS } from 'constants/api';
import rawAxios from 'axios';
import axios from 'utils/axios';

/** Start or reuse an authorized assessment evidence export. */
export const requestEvidencePackage = async (assessmentCode) => {
    const response = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/evidence-package`);
    return response.data;
};

/** Restore the latest evidence package and its background progress. */
export const getEvidencePackage = async (assessmentCode) => {
    const response = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/evidence-package`);
    return response.data;
};

/** Revalidate access and request a short-lived private ZIP download. */
export const downloadEvidencePackage = async (assessmentCode, processId) => {
    const response = await axios.post(
        `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/evidence-package/${encodeURIComponent(
            processId
        )}/download`
    );
    return response.data;
};

export const getCandidateAssessmentOptions = async () => {
    try {
        const result = await axios.get(CANDIDATE_ASSESSMENTS.v1.options);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment options');
    }
};

export const inviteCandidate = async (data) => {
    try {
        const result = await axios.post(CANDIDATE_ASSESSMENTS.v1.invite, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to invite candidate');
    }
};

export const getCandidateAssessments = async (params = {}) => {
    try {
        const result = await axios.get(CANDIDATE_ASSESSMENTS.v1.baseUrl, { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessments');
    }
};

/**
 * Resend a pending candidate assessment invitation
 * @param {string} assessmentCode - Public assessment code
 * @returns {Promise<Object>}
 */
export const resendCandidateAssessmentInvite = async (assessmentCode) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/resend-invite`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to resend candidate invitation');
    }
};

/**
 * Cancel a pending candidate assessment invitation
 * @param {string} assessmentCode - Public assessment code
 * @returns {Promise<Object>}
 */
export const cancelCandidateAssessmentInvite = async (assessmentCode) => {
    try {
        const result = await axios.patch(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/cancel-invite`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to cancel candidate invitation');
    }
};

/**
 * Set the candidate submission due date for an assessment
 * @param {string} assessmentCode - Public assessment code
 * @param {string} dueDate - Due date in YYYY-MM-DD format
 * @returns {Promise<Object>}
 */
export const setCandidateAssessmentDueDate = async (assessmentCode, dueDate) => {
    try {
        const result = await axios.patch(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/due-date`, {
            dueDate
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update assessment due date');
    }
};

export const getCandidateAssessment = async (assessmentCode, createLogs = true) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}`, {
            params: {
                createLogs
            }
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment');
    }
};

export const outcomeEvidenceInReview = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/outcome-evidence-in-review`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment');
    }
};

export const detachOutcomeEvidence = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/outcome-evidence-detach`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to detach evidence from outcome');
    }
};

export const acknowledgeOutcomeEvidenceGap = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/outcome-evidence-gap-acknowledgement`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to acknowledge the outcome evidence gap');
    }
};

export const getEvidenceFileAccessUrl = async (assessmentCode, evidenceId, mode = 'preview', options = {}) => {
    try {
        const url =
            options.sourcePhase === 'interviewMapping'
                ? `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-mapping/evidence/${evidenceId}/access-url`
                : `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/evidence/${evidenceId}/access-url`;
        const result = await axios.get(url, {
            params: {
                mode,
                sourceAssessmentId: options.sourceAssessmentId,
                sourceAssessmentCode: options.sourceAssessmentCode,
                sourcePhase: options.sourcePhase
            }
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create evidence access URL');
    }
};

export const getAssessmentReportAccessUrl = async (assessmentCode, reportId, mode = 'preview') => {
    try {
        const result = await axios.get(
            `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/reports/${encodeURIComponent(reportId)}/access-url`,
            {
                params: { mode }
            }
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create assessment report access URL');
    }
};

export const generateAssessmentReports = async (assessmentCode) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/reports/generate`);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to generate assessment reports');
    }
};

export const generateRplAuditReport = async (assessmentCode) => {
    try {
        const result = await axios.post(
            `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/audit-report/generate`
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to generate RPL audit report');
    }
};

export const outcomeEvidenceEvaluation = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/outcome-evidence-evaluation`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment');
    }
};

export const flagFollowUpSectionQuestion = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/flag-follow-up-section-question`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment');
    }
};

export const requestAdditionalEvidence = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/request-additional-evidence`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment');
    }
};

export const assessmentOutcomeOpen = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/outcome-open`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to logs outcome open');
    }
};

export const assessmentRequirementView = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/requirements-view`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to logs requirement view');
    }
};

export const assessmentNarrativeResponsesView = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/narrative-responses-view`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to logs narrative responses view');
    }
};

export const assessmentEvidenceEvaluationOpen = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/evidence-evaluation-open`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to logs evidence evaluation open');
    }
};

export const assessmentEvidenceEvaluationClose = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/evidence-evaluation-close`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to close evidence evaluation activity');
    }
};

export const assessmentAssessorReviewSubmit = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/assessor-review-submit`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit assessor review submit');
    }
};

export const updateAssessorReviewTiming = async (assessmentCode, data) => {
    try {
        const result = await axios.patch(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/assessor-review-timing`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update assessor review timing');
    }
};

export const claimAssessorReviewTimingSession = async (assessmentCode, data) => {
    try {
        const result = await axios.patch(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/assessor-review-timing-session`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to claim assessor review timing session');
    }
};

export const getAssessmentBehaviouralFlags = async (assessmentCode) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/behavioural-flags`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get behavioural flags');
    }
};

export const sendAssessorReviewTimingKeepalive = async (assessmentCode, data) => {
    try {
        await axios.patch(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/assessor-review-timing`, data);
        return true;
    } catch (error) {
        console.error('Failed to update assessor review timing', error);
        return false;
    }
};

export const getInterviewRequirements = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-requirements`, { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get interview requirements');
    }
};

export const getFeedbackRequirements = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(
            `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/feedback-requirements`,
            { params }
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get feedback requirements');
    }
};

export const getResubmissionGuidance = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-guidance`, { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get resubmission guidance');
    }
};

export const createResubmissionGuidance = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-guidance`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create resubmission guidance');
    }
};

export const updateResubmissionGuidance = async (assessmentCode, guidanceGroupId, data) => {
    try {
        const result = await axios.put(
            `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-guidance/${guidanceGroupId}`,
            data
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update resubmission guidance');
    }
};

export const deleteResubmissionGuidance = async (assessmentCode, guidanceGroupId) => {
    try {
        const result = await axios.delete(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-guidance/${guidanceGroupId}`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete resubmission guidance');
    }
};

export const submitResubmissionGuidance = async (assessmentCode) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-guidance-submit`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit resubmission guidance');
    }
};

export const completeInterviewReview = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-review-complete`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to complete interview review');
    }
};

export const createInterviewQuestion = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-questions`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create interview question');
    }
};

export const updateInterviewQuestion = async (assessmentCode, questionId, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-questions/${questionId}`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update interview question');
    }
};

export const deleteInterviewQuestion = async (assessmentCode, questionId) => {
    try {
        const result = await axios.delete(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-questions/${questionId}`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete interview question');
    }
};

export const reorderInterviewQuestions = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-questions/reorder`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to reorder interview questions');
    }
};

export const updateInterviewMappingReview = async (assessmentCode, mappingId, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-mappings/${mappingId}`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to save interview mapping review');
    }
};

export const reconcileInterviewScript = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-script/reconcile`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to reconcile interview script');
    }
};

export const getInterviewScriptQuestions = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(
            `${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${encodeURIComponent(assessmentCode)}/interview-script/questions`,
            { params }
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to get interview script questions');
    }
};

export const retryInterviewScriptEmail = async (assessmentCode) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-script/retry-email`);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to resend interview script email');
    }
};

export const getInterviewScriptAccessUrl = async (assessmentCode, version) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-script/${version}/access-url`);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to access interview script');
    }
};

export const requirementFinalDetermination = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/requirement-final-determination`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit requirement final determination');
    }
};

export const completeAssessment = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/complete-assessment`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to complete assessment');
    }
};

export const addAssessmentNotes = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/add-notes`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to add assessment notes');
    }
};

export const editAssessmentNotes = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/edit-notes`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit assessment notes');
    }
};

export const getActivityHistoryLogs = async (assessmentCode, data) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/get-activity-history-logs`, {
            params: data
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get candidate assessment activity history logs');
    }
};

export const assessmentResubmissionReviewSubmit = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/resubmission-review-submit`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit resubmission review submit');
    }
};

const candidateAssessmentUrl = (assessmentCode, path = '') =>
    `${CANDIDATE_ASSESSMENTS.v1.candidateBaseUrl}/${encodeURIComponent(assessmentCode)}${path}`;

export const getCandidateAssessmentStart = async (assessmentCode) => {
    try {
        const result = await axios.get(candidateAssessmentUrl(assessmentCode, '/start'));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load assessment');
    }
};

export const acknowledgeCandidateInstructions = async (assessmentCode) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/instructions-acknowledgement'));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to acknowledge assessment instructions');
    }
};

export const saveCandidateAssessmentResponses = async (assessmentCode, data) => {
    try {
        const result = await axios.patch(candidateAssessmentUrl(assessmentCode, '/responses'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to save assessment responses');
    }
};

export const createEvidenceUploadUrl = async (assessmentCode, data) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/evidence/upload-url'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create evidence upload URL');
    }
};

export const createEvidenceLibraryUploadUrl = async (assessmentCode, data) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/evidence/library/upload-url'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create evidence library upload URL');
    }
};

export const uploadEvidenceToS3 = async ({ uploadUrl, file, headers = {}, onUploadProgress }) => {
    await rawAxios.put(uploadUrl, file, {
        headers,
        withCredentials: false,
        onUploadProgress
    });
};

export const completeEvidenceUpload = async (assessmentCode, evidenceId, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, `/evidence/${evidenceId}/complete`), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to complete evidence upload');
    }
};

export const updateEvidenceMetadata = async (assessmentCode, evidenceId, data) => {
    try {
        const result = await axios.patch(
            candidateAssessmentUrl(assessmentCode, `/evidence/${encodeURIComponent(evidenceId)}/metadata`),
            data
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update evidence metadata');
    }
};

export const submitAdditionalEvidenceRequest = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/resubmission-requests/submit'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit additional evidence');
    }
};

export const recordResubmissionRequestActivity = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/resubmission-requests/activity'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to record resubmission request activity');
    }
};

export const getEvidenceLibrary = async (assessmentCode) => {
    try {
        const result = await axios.get(candidateAssessmentUrl(assessmentCode, '/evidence/library'));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get evidence library');
    }
};

export const linkCandidateAssessmentEvidence = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/evidence/link'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to link evidence');
    }
};

export const updateEvidence = async (assessmentCode, evidenceId, data = {}) => {
    try {
        const result = await axios.patch(candidateAssessmentUrl(assessmentCode, `/evidence/${evidenceId}`), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update evidence');
    }
};

export const deleteEvidence = async (assessmentCode, evidenceId, params = {}) => {
    try {
        const result = await axios.delete(candidateAssessmentUrl(assessmentCode, `/evidence/${evidenceId}`), { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete evidence');
    }
};

export const deleteEvidenceFile = async (assessmentCode, evidenceId) => {
    try {
        const result = await axios.delete(candidateAssessmentUrl(assessmentCode, `/evidence/${evidenceId}/file`));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete evidence file');
    }
};

export const getEvidenceAccessUrl = async (assessmentCode, evidenceId, mode = 'preview', options = {}) => {
    try {
        const result = await axios.get(candidateAssessmentUrl(assessmentCode, `/evidence/${evidenceId}/access-url`), {
            params: {
                mode,
                sourceAssessmentId: options.sourceAssessmentId,
                sourceAssessmentCode: options.sourceAssessmentCode
            }
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create evidence access URL');
    }
};

export const submitCandidateAssessment = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/submit'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to submit assessment');
    }
};

/**
 * This will be used in candidate login
 */
export const getCandidateAssessmentInfo = async (assessmentCode) => {
    try {
        const result = await axios.get(candidateAssessmentUrl(assessmentCode, ''));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load assessment');
    }
};

export const getCandidateWorkExperience = async (assessmentCode) => {
    try {
        const result = await axios.get(candidateAssessmentUrl(assessmentCode, '/work-experience'));
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load work experience');
    }
};

export const saveCandidateWorkExperience = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.put(candidateAssessmentUrl(assessmentCode, '/work-experience'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to save work experience');
    }
};

export const createCandidateWorkExperience = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(candidateAssessmentUrl(assessmentCode, '/work-experience'), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create workplace record');
    }
};

export const updateCandidateWorkExperience = async (assessmentCode, workplaceRecordId, data = {}) => {
    try {
        const result = await axios.patch(candidateAssessmentUrl(assessmentCode, `/work-experience/${workplaceRecordId}`), data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update workplace record');
    }
};

/**
 * Generate resubmission guidance
 * @param {*} data
 * @returns
 */
export const generateResubmissionGuidance = async (data = {}) => {
    try {
        const payload = typeof data === 'string' ? { assessmentCode: data } : data || {};
        const result = await axios.post(CANDIDATE_ASSESSMENTS.v1.generateResubmissionGuidance, payload);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to generate resubmission guidance');
    }
};

/**
 * Generate interview questions
 * @param {*} data
 * @returns
 */
export const generateInterviewQuestions = async (data = {}) => {
    try {
        const payload = typeof data === 'string' ? { assessmentCode: data } : data || {};
        const result = await axios.post(CANDIDATE_ASSESSMENTS.v1.generateInterviewQuestions, payload);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to generate interview questions');
    }
};

/**
 * Check Valid Transcription or Not
 * @param {*} data
 * @returns
 */
export const checkValidTranscription = async (data = {}) => {
    try {
        const result = await axios.post(CANDIDATE_ASSESSMENTS.v1.checkValidTranscript, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to check if valid transcript or not');
    }
};

/**
 * Generate Interview Transcript
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const generateInterviewTranscript = async (assessmentCode, data = {}) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-recording-transcript`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to generate interview transcript');
    }
};

/**
 * Generate Interview Transcript Segmentation and Mapping
 * @param {string} assessmentCode
 * @returns
 */
export const generateInterviewTranscriptSegmentation = async (assessmentCode) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/interview-transcript-segmentation`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to generate interview transcript mapping');
    }
};

/**
 * Get Interview Recording File Pre Signed Access Url
 * @param {string} assessmentCode
 * @returns
 */
export const getRecordingFileAccessUrl = async (assessmentCode) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/recording-access-url`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create interview recording access URL');
    }
};

/**
 * Edit single interview recording transcript
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const editInterviewTranscript = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/edit-interview-transcript`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit transcript segment');
    }
};

/**
 * Edit multiple interview recording transcript
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const editMultipleInterviewTranscript = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/edit-multiple-interview-transcript`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit multiple transcript segment');
    }
};

/**
 * Edit candidate assessment closure status
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const editAssessmentClosureStatus = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/edit-closure-status`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit assessment closure status');
    }
};

/**
 * Edit candidate assessment closed or reopened
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const editAssessmentClosedReopen = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/closed-or-reopen`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit assessment closed or reopen');
    }
};

/**
 * Get the information of appeal details
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const getAppealDetails = async (assessmentCode) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/appeal-details`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get appeal details');
    }
};

/**
 * Approve or deny appeal request from candidate
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const appealDetailsApproval = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/appeal-details-approval`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || `Failed to ${data?.action} for candidate appeal request`);
    }
};

/**
 * Appeal assessment for requirement
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const requirementAppealAssessment = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/requirement-appeal-assessment`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit requirement appeal assessment');
    }
};

/**
 * Complete Appeal Assessment
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const completeAppealAssessment = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/complete-appeal-assessment`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to complete assessment');
    }
};

/**
 * Get paginated activity logs for all assessments or assessor assigned assessments
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const getActivityLogs = async (data) => {
    try {
        const result = await axios.get(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/get-activity-logs`, {
            params: data
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get assessment activity logs');
    }
};

/**
 * Add a manual transcript for an Interview Mapping Review
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const addManualTranscript = async (assessmentCode, data) => {
    try {
        const result = await axios.post(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/add-manual-transcript`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to add manual transcript');
    }
};

/**
 * Edit a manual transcript for an Interview Mapping Review
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const editManualTranscript = async (assessmentCode, data) => {
    try {
        const result = await axios.put(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/edit-manual-transcript`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to edit manual transcript');
    }
};

/**
 * Delete a manual transcript for an Interview Mapping Review
 * @param {string} assessmentCode
 * @param {*} data
 * @returns
 */
export const deleteManualTranscript = async (assessmentCode, data) => {
    try {
        const result = await axios.delete(`${CANDIDATE_ASSESSMENTS.v1.baseUrl}/${assessmentCode}/delete-manual-transcript`, {
            params: data
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete manual transcript');
    }
};
